import { useMemo, type ReactNode } from 'react';
import { defaultAudioSettings, type AudioSettings, type Clip } from '@cinegrid/core';
import { resolveAudioSettings } from '../../engine/audioFx';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel, SliderRow } from '../controls/PanelShell';

function FxSectionHeader({
  children,
  checked,
  onChange,
}: {
  children: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-hairline px-2 py-1.5">
      <span className="text-2xs font-semibold uppercase tracking-wider text-text-low">
        {children}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={`${String(children)} enabled`}
        className="accent-[var(--cg-accent)]"
      />
    </div>
  );
}

function AudioControls({ clip }: { clip: Clip }) {
  const updateClipAudio = useEditorStore((s) => s.updateClipAudio);
  const audio = resolveAudioSettings(clip.properties.audio);

  const patch = (partial: Partial<AudioSettings>) => updateClipAudio(clip.id, partial);
  const defaults = defaultAudioSettings();

  return (
    <PanelShell
      title="Audio"
      actions={
        <button
          type="button"
          className="text-2xs text-text-low hover:text-accent"
          onClick={() => updateClipAudio(clip.id, defaultAudioSettings())}
        >
          Reset
        </button>
      }
    >
      <SectionLabel>Volume</SectionLabel>
      <SliderRow
        label="Level"
        value={audio.volume}
        min={0}
        max={2}
        defaultValue={defaults.volume}
        onChange={(volume) => patch({ volume })}
        format={(v) => `${Math.round(v * 100)}%`}
      />

      <FxSectionHeader
        checked={audio.noiseRemoval.enabled}
        onChange={(enabled) =>
          patch({ noiseRemoval: { ...audio.noiseRemoval, enabled } })
        }
      >
        Noise Removal
      </FxSectionHeader>
      {audio.noiseRemoval.enabled && (
        <SliderRow
          label="Amount"
          value={audio.noiseRemoval.amount}
          min={0}
          max={1}
          defaultValue={defaults.noiseRemoval.amount}
          onChange={(amount) =>
            patch({ noiseRemoval: { ...audio.noiseRemoval, amount } })
          }
        />
      )}

      <FxSectionHeader
        checked={audio.eq.enabled}
        onChange={(enabled) => patch({ eq: { ...audio.eq, enabled } })}
      >
        EQ
      </FxSectionHeader>
      {audio.eq.enabled && (
        <>
          <SliderRow
            label="Low"
            value={audio.eq.low}
            min={-12}
            max={12}
            step={0.5}
            defaultValue={0}
            onChange={(low) => patch({ eq: { ...audio.eq, low } })}
            format={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)} dB`}
          />
          <SliderRow
            label="Mid"
            value={audio.eq.mid}
            min={-12}
            max={12}
            step={0.5}
            defaultValue={0}
            onChange={(mid) => patch({ eq: { ...audio.eq, mid } })}
            format={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)} dB`}
          />
          <SliderRow
            label="High"
            value={audio.eq.high}
            min={-12}
            max={12}
            step={0.5}
            defaultValue={0}
            onChange={(high) => patch({ eq: { ...audio.eq, high } })}
            format={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)} dB`}
          />
        </>
      )}

      <FxSectionHeader
        checked={audio.compressor.enabled}
        onChange={(enabled) =>
          patch({ compressor: { ...audio.compressor, enabled } })
        }
      >
        Compressor
      </FxSectionHeader>
      {audio.compressor.enabled && (
        <>
          <SliderRow
            label="Threshold"
            value={audio.compressor.threshold}
            min={-60}
            max={0}
            step={1}
            defaultValue={defaults.compressor.threshold}
            onChange={(threshold) =>
              patch({ compressor: { ...audio.compressor, threshold } })
            }
            format={(v) => `${v | 0} dB`}
          />
          <SliderRow
            label="Ratio"
            value={audio.compressor.ratio}
            min={1}
            max={20}
            step={0.5}
            defaultValue={defaults.compressor.ratio}
            onChange={(ratio) =>
              patch({ compressor: { ...audio.compressor, ratio } })
            }
            format={(v) => `${v.toFixed(1)}:1`}
          />
          <SliderRow
            label="Attack"
            value={audio.compressor.attack}
            min={0.001}
            max={0.2}
            step={0.001}
            defaultValue={defaults.compressor.attack}
            onChange={(attack) =>
              patch({ compressor: { ...audio.compressor, attack } })
            }
            format={(v) => `${(v * 1000).toFixed(0)} ms`}
          />
          <SliderRow
            label="Release"
            value={audio.compressor.release}
            min={0.01}
            max={1}
            step={0.01}
            defaultValue={defaults.compressor.release}
            onChange={(release) =>
              patch({ compressor: { ...audio.compressor, release } })
            }
            format={(v) => `${(v * 1000).toFixed(0)} ms`}
          />
          <SliderRow
            label="Makeup"
            value={audio.compressor.makeup}
            min={0}
            max={1}
            defaultValue={defaults.compressor.makeup}
            onChange={(makeup) =>
              patch({ compressor: { ...audio.compressor, makeup } })
            }
          />
        </>
      )}

      <FxSectionHeader
        checked={audio.reverb.enabled}
        onChange={(enabled) => patch({ reverb: { ...audio.reverb, enabled } })}
      >
        Reverb
      </FxSectionHeader>
      {audio.reverb.enabled && (
        <>
          <SliderRow
            label="Mix"
            value={audio.reverb.amount}
            min={0}
            max={1}
            defaultValue={defaults.reverb.amount}
            onChange={(amount) => patch({ reverb: { ...audio.reverb, amount } })}
          />
          <SliderRow
            label="Size"
            value={audio.reverb.size}
            min={0}
            max={1}
            defaultValue={defaults.reverb.size}
            onChange={(size) => patch({ reverb: { ...audio.reverb, size } })}
          />
        </>
      )}
    </PanelShell>
  );
}

export function AudioPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);

  const clip = useMemo(
    () => project.clips.find((c) => c.id === selection[0]),
    [project.clips, selection]
  );

  if (!clip) {
    return (
      <PanelShell title="Audio">
        <p className="p-3 text-2xs text-text-low">Select a clip.</p>
      </PanelShell>
    );
  }

  if (clip.kind !== 'audio' && clip.kind !== 'video') {
    return (
      <PanelShell title="Audio">
        <p className="p-3 text-2xs text-text-low">
          Audio settings apply to video and audio clips.
        </p>
      </PanelShell>
    );
  }

  return <AudioControls clip={clip} />;
}
